"use client";

import { useState, useCallback } from "react";
import { toast } from "sonner";
import { validateFile } from "@/app/utils/helpers/backend/files/file-validation";
import {
  uploadPostImageAction,
  deletePostImageAction,
} from "@/app/services/server/actions/posts/posts-images";
import {
  uploadContactPersonImageAction,
  deleteContactPersonImageAction,
} from "@/app/services/server/actions/contact-persons/contact-person-images";
import {
  uploadCampaignImageAction,
  deleteCampaignImageAction,
} from "@/app/services/server/actions/campaigns/campaign-images";
import {
  uploadMakeImageAction,
  deleteMakeImageAction,
} from "@/app/services/server/actions/makes/make-images";
import {
  uploadModelImageAction,
  deleteModelImageAction,
} from "@/app/services/server/actions/models/model-images";
import {
  uploadOfferImageAction,
  deleteOfferImageAction,
} from "@/app/services/server/actions/offers/offer-images";
import {
  uploadUserProfileImageAction,
  deleteUserProfileImageAction,
} from "@/app/services/server/actions/users/profile-image";
import type {
  ImageTypeT,
  ImageEntityTypeT,
  PostImageTypeT,
  ContactPersonImageTypeT,
  CampaignImageTypeT,
  MakeImageTypeT,
  ModelImageTypeT,
  OfferImageTypeT,
} from "@/app/types/images";

// Image upload configuration type
type ImageUploadConfigT = {
  entityType: ImageEntityTypeT;
  entityId: string;
  imageType: ImageTypeT;
  currentAltText?: string | null;
  onImageChange: (image: { url: string; alt: string } | null) => void;
};

type UseImageUploadPropsT = ImageUploadConfigT;

/**
 * Custom hook for reusable image upload and delete operations
 *
 * Handles all image-related operations across different entities including:
 * - File validation using centralized validation utils
 * - Upload with progress tracking for posts and contact persons
 * - Delete operations with entity-specific actions
 * - Error handling with toast notifications
 * - Loading state management
 *
 * @param props - Hook configuration with entity type
 * @returns Upload/delete functions and state
 *
 * @example
 * ```typescript
 * // For posts
 * const { isUploading, isDeleting, handleFileUpload, handleImageDelete } =
 *   useImageUpload({
 *     entityType: "posts",
 *     entityId: postId,
 *     imageType: "thumbnail",
 *     currentAltText,
 *     onImageChange
 *   });
 *
 * // For contact persons
 * const { isUploading, isDeleting, handleFileUpload, handleImageDelete } =
 *   useImageUpload({
 *     entityType: "contact_persons",
 *     entityId: contactPersonId,
 *     imageType: "profile",
 *     currentAltText,
 *     onImageChange
 *   });
 * ```
 */
export function useImageUpload({
  entityType,
  entityId,
  imageType,
  currentAltText,
  onImageChange,
}: UseImageUploadPropsT) {
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Handle file upload with entity-specific actions
  const handleFileUpload = useCallback(
    async (file: File) => {
      if (!file) return;

      // Validate file using centralized validation
      const validationError = validateFile(file);
      if (validationError) {
        toast.error(validationError);
        return;
      }

      setIsUploading(true);

      try {
        let result: {
          success: boolean;
          data?: { url: string; alt: string };
          error?: string;
        };

        switch (entityType) {
          case "posts": {
            // Create FormData for post image upload
            const formData = new FormData();
            formData.append("file", file);
            formData.append("postId", entityId);
            formData.append("imageType", imageType);
            formData.append("altText", currentAltText || "");

            result = await uploadPostImageAction(formData);
            break;
          }
          case "contact_persons": {
            // Upload contact person image directly
            result = await uploadContactPersonImageAction(
              file,
              entityId,
              imageType as ContactPersonImageTypeT,
              currentAltText || ""
            );
            break;
          }
          case "campaigns": {
            // Create FormData for campaign image upload
            const formData = new FormData();
            formData.append("file", file);
            formData.append("campaignId", entityId);
            formData.append("imageType", imageType);
            formData.append("altText", currentAltText || "");

            result = await uploadCampaignImageAction(formData);
            break;
          }
          case "makes": {
            // Upload make image directly
            result = await uploadMakeImageAction(
              file,
              entityId,
              imageType as MakeImageTypeT,
              currentAltText || ""
            );
            break;
          }
          case "models": {
            // Upload model image directly
            result = await uploadModelImageAction(
              file,
              entityId,
              imageType as ModelImageTypeT,
              currentAltText || ""
            );
            break;
          }
          case "offers": {
            // Upload offer image directly
            result = await uploadOfferImageAction(
              file,
              entityId,
              imageType as OfferImageTypeT,
              currentAltText || ""
            );
            break;
          }
          case "users": {
            // Upload user profile image directly
            result = await uploadUserProfileImageAction(file, entityId);
            break;
          }
          default:
            throw new Error(`Unsupported entity type: ${entityType}`);
        }

        if (result.success && result.data) {
          onImageChange({
            url: result.data.url,
            alt: result.data.alt,
          });
          toast.success("Bild erfolgreich hochgeladen");
        } else {
          const errorMessage = result.error || "Upload fehlgeschlagen";
          toast.error(errorMessage);
        }
      } catch (err) {
        console.error("Upload error:", err);
        toast.error("Unerwarteter Fehler beim Upload");
      } finally {
        setIsUploading(false);
      }
    },
    [entityType, entityId, imageType, currentAltText, onImageChange]
  );

  // Handle image deletion with entity-specific actions
  const handleImageDelete = useCallback(async () => {
    setIsDeleting(true);

    try {
      let result: { success: boolean; error?: string };

      switch (entityType) {
        case "posts":
          result = await deletePostImageAction(
            entityId,
            imageType as PostImageTypeT
          );
          break;
        case "contact_persons":
          result = await deleteContactPersonImageAction(entityId);
          break;
        case "campaigns":
          result = await deleteCampaignImageAction(
            entityId,
            imageType as CampaignImageTypeT
          );
          break;
        case "makes":
          result = await deleteMakeImageAction(entityId);
          break;
        case "models":
          result = await deleteModelImageAction(entityId);
          break;
        case "offers":
          result = await deleteOfferImageAction(
            entityId,
            imageType as OfferImageTypeT
          );
          break;
        case "users":
          result = await deleteUserProfileImageAction(entityId);
          break;
        default:
          throw new Error(`Unsupported entity type: ${entityType}`);
      }

      if (result.success) {
        onImageChange(null);
        toast.success("Bild erfolgreich entfernt");
      } else {
        const errorMessage = result.error || "Löschen fehlgeschlagen";
        toast.error(errorMessage);
      }
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("Unerwarteter Fehler beim Löschen");
    } finally {
      setIsDeleting(false);
    }
  }, [entityType, entityId, imageType, onImageChange]);

  return {
    isUploading,
    isDeleting,
    handleFileUpload,
    handleImageDelete,
  };
}
